// inputverbs.ts — touch→mouse rewrite for the in-game HELP MANUAL.
//
// The manual (data/help.txt, one shared doc served to every form factor) walks
// the player through gestures in prose — "tap", "double tap", "tap and hold",
// "drag … with your finger". That reads right on a phone/tablet (the
// FingerHandler) but wrong on a mouse-driven desktop (gfMouseMode → the
// StylusHandler), where the same gestures are click / double-click /
// click-and-hold. Rather than fork help.txt per device, we rewrite the input
// VERBS at the display boundary when the local player is in mouse mode.
//
// The MISSION copy (E-Com / objective banner / briefing) is NOT rewritten here:
// it is authored per device in the level data itself (S_01's $iphone-gated touch
// vs mouse ecoms + device-neutral objective text), selected by the $iphone
// trigger sysvar. This module is the manual-only remainder.
//
// Pure presentation: only what the player reads changes. Touch devices
// (fMouseMode === false) see the original authored phrasing verbatim.

// Live mirror of GameSession.options.fMouseMode (gfMouseMode — the same flag the
// StylusHandler/FingerHandler split reads). Module-level, like
// render/debugoverlay.ts dbgOverlay, so the diverse display sites (E-Com,
// objective banner, OBJECTIVES briefing) can localize without threading a flag
// through every seam. GameSession keeps it current (construction + the in-game
// options apply).
let gfMouseMode = false;

/** Mirror the live gfMouseMode so {@link localizeInstructions} knows which form
 *  factor the local player is using. */
export function setInstructionMouseMode(fMouseMode: boolean): void {
  gfMouseMode = fMouseMode;
}

// Apply the matched fragment's capitalization to the replacement: ALL-CAPS →
// upper, Leading-capital → capitalize the first letter, otherwise verbatim.
function matchCase(replacement: string, matched: string): string {
  const fHasLetter = /[a-z]/i.test(matched);
  if (fHasLetter && matched === matched.toUpperCase()) return replacement.toUpperCase();
  if (matched[0] === matched[0].toUpperCase()) {
    return replacement[0].toUpperCase() + replacement.slice(1);
  }
  return replacement;
}

// Ordered touch→mouse rewrites. Compound phrases come first so "double tap" and
// "tap and hold" are consumed before the bare-"tap" rule reaches them. The \b
// word boundaries keep "tap"/"finger" from matching inside unrelated words.
const kaRewrites: { re: RegExp; to: string }[] = [
  { re: /\bdouble[-\s]?tapping\b/gi, to: 'double-clicking' },
  { re: /\bdouble[-\s]?tapped\b/gi, to: 'double-clicked' },
  { re: /\bdouble[-\s]?taps\b/gi, to: 'double-clicks' },
  { re: /\bdouble[-\s]?tap\b/gi, to: 'double-click' },
  { re: /\btap\s+and\s+hold\b/gi, to: 'click and hold' },
  { re: /\btap-?hold\b/gi, to: 'click and hold' },
  { re: /\btapping\b/gi, to: 'clicking' },
  { re: /\btapped\b/gi, to: 'clicked' },
  { re: /\btaps\b/gi, to: 'clicks' },
  { re: /\btap\b/gi, to: 'click' },
  { re: /\bwith your fingers?\b/gi, to: 'with your mouse' },
  { re: /\byour fingers?\b/gi, to: 'your mouse' },
];

/** Rewrite touch input verbs to their mouse equivalents WHEN the local player is
 *  in mouse mode; a no-op (returns the input unchanged) on touch devices. */
export function localizeInstructions(text: string): string {
  if (!gfMouseMode || text === '') return text;
  let out = text;
  for (const r of kaRewrites) {
    out = out.replace(r.re, (m) => matchCase(r.to, m));
  }
  return out;
}
